import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { TruncatePipe } from '../../basic/pipes/truncate.pipe';
import { Company } from '../../core/company/company';
import { CompanyService } from '../../core/company/company.service';
import { PersonalityService } from '../../core/personality/personality.service';
import { Resume, ResumeSource, ResumeType } from '../../core/resume/resume';
import { ResumeService } from '../../core/resume/resume.service';
import { NewEditCompanyComponent } from '../../domain/company/new-edit-company/new-edit-company.component';
import { AddPersonalityCardComponent } from '../../domain/personality/add-personality-card/add-personality-card.component';
import { PersonalityCardComponent } from '../../domain/personality/personality-card/personality-card.component';
import { ProfileCardSmallComponent } from '../../domain/profile-card/profile-card-small/profile-card-small.component';
import { ProfileCardComponent } from '../../domain/profile-card/profile-card.component';
import { EditResumeComponent } from '../../domain/resume/edit-resume/edit-resume.component';
import { GenerateResumeTextInputComponent } from '../../domain/resume/generate-resume-text-input/generate-resume-text-input.component';
import { ResumeListTableComponent } from '../../domain/resume/resume-list-table/resume-list-table.component';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';
import { MainSidebarComponent } from '../_shared/main-sidebar/main-sidebar.component';

@Component({
  selector: 'app-resume',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzInputModule,
    NzIconModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzPopconfirmModule,
    NzTagModule,
    NzTabsModule,

    TruncatePipe,

    MatTooltip,

    BasicPageComponent,
    MainSidebarComponent,

    EditResumeComponent,
    ProfileCardSmallComponent,
    ProfileCardComponent,
    PersonalityCardComponent,
    AddPersonalityCardComponent,
    GenerateResumeTextInputComponent,
    NewEditCompanyComponent,
    ResumeListTableComponent,
  ],
  templateUrl: './resume.component.html',
  styleUrl: './resume.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResumeComponent {
  selectedItem = signal<Resume | undefined>(undefined);

  isGenerateVisible = false;
  isNewEditVisible = false;
  isEdit = false;

  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  companyList = toSignal(this.companyService.itemList$(), { initialValue: [] });

  personalityItemList = toSignal(this.personalityService.itemList$(), { initialValue: [] });

  realResumeList = computed(() => {
    return this.resumeList().filter((item) => item.type === ResumeType.real);
  });

  aiResumeList = computed(() => {
    return this.resumeList().filter((item) => item.type !== ResumeType.ai);
  });

  constructor(
    private companyService: CompanyService,
    private resumeService: ResumeService,
    private personalityService: PersonalityService
  ) {}

  newResume?: Resume = undefined;

  onNewBtnClick() {
    this.isEdit = false;
    this.isNewEditVisible = true;
    this.newResume = {
      subData: {
        source: ResumeSource.manual,
        content: {},
      },
    } as any;
  }

  onGenerateBtnClick() {
    this.isGenerateVisible = true;
  }

  // Company
  isNewEditCompanyVisible = false;
  selectedCompany?: Company = undefined;

  expandSet = new Set<string>();
  onExpandChange(id: string, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }
  onExpandClick(id: string) {
    this.onExpandChange(id, !this.expandSet.has(id));
  }

  onNewCompanyBtnClick() {
    this.selectedCompany = undefined;
    this.isNewEditCompanyVisible = true;
  }

  onEditCompanyClick(data: Company) {
    this.selectedCompany = data;
    this.isNewEditCompanyVisible = true;
  }

  onDeleteCompanyClick(data: Company) {
    this.companyService
      .delete$([data])
      .pipe(take(1))
      .subscribe(() => {});
  }

  getCustomerListByCompanyId = (companyId?: string) =>
    computed(() =>
      this.resumeList().filter((resume) => {
        if (!companyId) {
          return true;
        }
        return resume.parentId === companyId;
      })
    );
}
